import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import {
  selectScheduleMutationBlocked,
  selectScheduleData,
  selectScheduleConstraints,
  selectScheduleSelectedItem,
  selectScheduleCurrentFilters,
  selectScheduleIsCreating,
  selectScheduleIsGeneratingAsync,
  selectScheduleJobQueuePosition,
  selectScheduleJobEtaSeconds,
  selectScheduleItemScaleMultiplier,
  selectScheduleColumnWidth,
  selectScheduleTournamentInfo,
  selectScheduleEditorFullscreen,
  selectScheduleHasUnpublishedChanges,
  selectScheduleIsFreeEditMode,
  selectScheduleSelectedMatchIds,
  selectBulkSelectableUnscheduledMatchIds,
  selectBulkSelectionHasScheduled,
  selectBulkSelectionScheduledIds,
} from '../../../../../features/schedule/scheduleSelectors';
import { selectBulkSelectableMatchIdsForDay } from '../../../../../features/schedule/scheduleGridSelectors';
import {
  setDisplayScheduleState,
  setSelectedItem,
  clearScheduleHistory,
  clearScheduleSelection,
  setScheduleSelection,
  setScheduleItemScaleMultiplier,
  setScheduleColumnWidth,
  setScheduleState,
  setFreeEditMode,
  redoScheduleChange,
  undoScheduleChange,
} from '../../../../../features/schedule/scheduleSlice';
import { CustomButton, CustomSpinner } from '../../../../../components';
import { Notification } from '../../../../../hooks/useNotification';
import { TypeError } from '../../../../../types/enums';
import { scrollToScheduleMatchWithRetry } from './scrollToScheduleMatch';
import { IMatchScheduleItem, isFreeSlot } from '../../../../../types/schedule';
import {
  rebuildEntireSchedule,
  getTournamentTimezone,
  scheduleHasOutOfWindowMatches,
} from '../schedule.helpers';
import { flattenScheduledMatches } from '../../../../../features/schedule/scheduleOverlap';
import { deriveDisplayScheduleView, resolveScheduleDayKey } from '../scheduleDisplayView';
import { cancelScheduleAction, bulkRemoveMatchesFromScheduleAction, remediateScheduleTimezoneAction } from '../../../../../features/schedule/scheduleActions';
import {
  areScheduleZoomFloorsEqual,
  clampScheduleColumnWidthWithFloors,
  clampScheduleScaleWithFloors,
  mergeScheduleZoomFloors,
  SCHEDULE_ZOOM_COLUMN_WIDTH_MIN,
  SCHEDULE_ZOOM_SCALE_MIN,
  type ScheduleZoomFloors,
} from '../../../../../features/schedule/scheduleZoomBounds';
import { ScheduleToolbarButton } from './ScheduleToolbarButton';
import { ScheduleToolbar } from './ScheduleToolbar';
import { ScheduleZoomRoot } from './ScheduleZoomRoot';
import type { ScheduleLeftRailTab } from './ScheduleLeftRail';
import {
  selectScheduleConflictCount,
  selectScheduleConflictMatchIdList,
} from '../../../../../features/schedule/scheduleConflictSelectors';
import type { ScheduleConflict } from '../../../../../features/schedule/scheduleConflictDetector';
import { detectScheduleTimezoneStale } from '../../../../../features/schedule/scheduleDayAlign';
import { openModal } from '../../../../../features/modal/modalSlice';
import { ModalAction } from '../../../../../types/modal';
import { hasScheduleOccupancyFilters } from '../schedule.helpers';
import {
  blurScheduleToolbarFilters,
  dismissScheduleToolbarFilterOnEscape,
  isScheduleHistoryKeyTarget,
  isScheduleToolbarFilterTarget,
  useScheduleBreakpointMobile,
} from './scheduleLayoutHooks';
import {
  selectScheduleCanRedo,
  selectScheduleCanUndo,
} from '../../../../../features/schedule/scheduleSelectors';
import './ScheduleWorkbench.scss';

const DEFAULT_SCHEDULE_ZOOM_FLOORS: ScheduleZoomFloors = {
  minScale: SCHEDULE_ZOOM_SCALE_MIN,
  minColumnWidth: SCHEDULE_ZOOM_COLUMN_WIDTH_MIN,
  viewportWidth: 0,
  viewportHeight: 0,
  courtCount: 0,
  slotCount: 0,
};

export const ScheduleLayout = () => {
  const { t } = useTranslation();
  const { communityId } = useParams<{ communityId: string }>();
  const prefix = 'creativeDetail-';
  const dispatch = useAppDispatch();
  const schedule = useAppSelector(selectScheduleData);
  const scheduleConstraints = useAppSelector(selectScheduleConstraints);
  const selectedItem = useAppSelector(selectScheduleSelectedItem);
  const currentFilters = useAppSelector(selectScheduleCurrentFilters);
  const isCreatingSchedule = useAppSelector(selectScheduleIsCreating);
  const isGeneratingAsync = useAppSelector(selectScheduleIsGeneratingAsync);
  const jobQueuePosition = useAppSelector(selectScheduleJobQueuePosition);
  const jobEtaSeconds = useAppSelector(selectScheduleJobEtaSeconds);
  const scheduleItemScaleMultiplier = useAppSelector(selectScheduleItemScaleMultiplier);
  const scheduleColumnWidth = useAppSelector(selectScheduleColumnWidth);
  const tournamentInfo = useAppSelector(selectScheduleTournamentInfo);
  const scheduleEditorFullscreen = useAppSelector(selectScheduleEditorFullscreen);
  const hasUnpublishedChanges = useAppSelector(selectScheduleHasUnpublishedChanges);
  const isFreeEditMode = useAppSelector(selectScheduleIsFreeEditMode);
  const canUndoSchedule = useAppSelector(selectScheduleCanUndo);
  const canRedoSchedule = useAppSelector(selectScheduleCanRedo);
  const scheduleMutationBlocked = useAppSelector(selectScheduleMutationBlocked);
  const selectedMatchIds = useAppSelector(selectScheduleSelectedMatchIds);
  const bulkSelectionScheduledIds = useAppSelector(selectBulkSelectionScheduledIds);
  const bulkSelectionHasScheduled = useAppSelector(selectBulkSelectionHasScheduled);
  const scheduleConflictCount = useAppSelector(selectScheduleConflictCount);
  const scheduleConflictMatchIdList = useAppSelector(selectScheduleConflictMatchIdList);
  const [showScheduleConflicts, setShowScheduleConflicts] = useState(false);
  const [blockInvalidPlacement, setBlockInvalidPlacement] = useState(false);
  const [outOfWindowBannerDismissed, setOutOfWindowBannerDismissed] = useState(false);
  const [timezoneStaleBannerDismissed, setTimezoneStaleBannerDismissed] = useState(false);
  const [isSyncingScheduleTimezone, setIsSyncingScheduleTimezone] = useState(false);
  const [leftRailTab, setLeftRailTab] = useState<ScheduleLeftRailTab>('unscheduled');
  const [selectedDayKey, setSelectedDayKey] = useState<number>(() =>
    schedule && schedule.days.length > 0 ? schedule.days[0].dayKey : 0
  );
  const [focusedConflictMatchId, setFocusedConflictMatchId] = useState<number | null>(null);
  const [focusedConflictPeerIds, setFocusedConflictPeerIds] = useState<number[]>([]);
  const [conflictFocusPulseGeneration, setConflictFocusPulseGeneration] = useState(0);
  const suppressFocusClearOnDayChangeRef = useRef(false);

  const previousTournamentIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const tid = tournamentInfo?.id;
    if (tid == null || !schedule?.days?.length) return;
    if (previousTournamentIdRef.current !== tid) {
      previousTournamentIdRef.current = tid;
      setSelectedDayKey(schedule.days[0].dayKey);
    }
  }, [schedule, tournamentInfo?.id]);

  const scheduleTimeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);

  const hasOutOfWindowMatches = useMemo(
    () => scheduleHasOutOfWindowMatches(schedule, scheduleConstraints, scheduleTimeZone),
    [schedule, scheduleConstraints, scheduleTimeZone]
  );

  const hasScheduleTimezoneStale = useMemo(
    () => detectScheduleTimezoneStale(scheduleConstraints, scheduleTimeZone),
    [scheduleConstraints, scheduleTimeZone]
  );

  useEffect(() => {
    setOutOfWindowBannerDismissed(false);
  }, [tournamentInfo?.id, hasOutOfWindowMatches]);

  useEffect(() => {
    setTimezoneStaleBannerDismissed(false);
  }, [tournamentInfo?.id, hasScheduleTimezoneStale]);

  const showConflictsToggleVisible =
    scheduleConflictCount > 0 || hasScheduleOccupancyFilters(currentFilters);

  const effectiveSelectedDayKey = useMemo(
    () => resolveScheduleDayKey(schedule, selectedDayKey),
    [schedule, selectedDayKey]
  );

  const dayBulkSelectableMatchIds = useAppSelector((state) =>
    selectBulkSelectableMatchIdsForDay(state, effectiveSelectedDayKey)
  );

  const unscheduledBulkSelectableMatchIds = useAppSelector(
    selectBulkSelectableUnscheduledMatchIds
  );

  useEffect(() => {
    if (selectedMatchIds.length === 0) return;
    const visibleOnDay = new Set(dayBulkSelectableMatchIds);
    const unscheduledIds = new Set(unscheduledBulkSelectableMatchIds);
    const pruned = selectedMatchIds.filter(
      (id) => visibleOnDay.has(id) || unscheduledIds.has(id)
    );
    if (pruned.length !== selectedMatchIds.length) {
      dispatch(setScheduleSelection(pruned));
    }
  }, [
    effectiveSelectedDayKey,
    dayBulkSelectableMatchIds,
    unscheduledBulkSelectableMatchIds,
    dispatch,
    selectedMatchIds,
  ]);

  useEffect(() => {
    if (effectiveSelectedDayKey !== selectedDayKey) {
      setSelectedDayKey(effectiveSelectedDayKey);
    }
  }, [effectiveSelectedDayKey, selectedDayKey]);

  const displayView = useMemo(
    () => deriveDisplayScheduleView(schedule, currentFilters, effectiveSelectedDayKey),
    [schedule, currentFilters, effectiveSelectedDayKey]
  );

  const displayViewStructureKey = useMemo(
    () =>
      displayView?.days.map((d) => `${d.dayKey}:${d.courts.length}`).join('|') ?? '',
    [displayView?.days]
  );

  const scheduleContentKey = useMemo(
    () =>
      flattenScheduledMatches(schedule)
        .map((m) => `${m.id}:${m.scheduleDate ?? ''}:${m.startDate ?? ''}:${m.court ?? ''}`)
        .join('|'),
    [schedule]
  );

  useEffect(() => {
    let currentSchedule = schedule;

    if (
      (!currentSchedule || currentSchedule.days.length === 0) &&
      scheduleConstraints &&
      tournamentInfo
    ) {
      const existingMatches =
        currentSchedule?.days?.flatMap((day) =>
          day.courts.flatMap(
            (court) => court.matches.filter((m) => !isFreeSlot(m)) as IMatchScheduleItem[]
          )
        ) || [];

      currentSchedule = rebuildEntireSchedule(existingMatches, scheduleConstraints, scheduleTimeZone);
      dispatch(clearScheduleHistory());
      dispatch(setScheduleState(currentSchedule));
    }
  }, [schedule, scheduleConstraints, scheduleTimeZone, tournamentInfo, dispatch]);

  useEffect(() => {
    const view = deriveDisplayScheduleView(schedule, currentFilters, effectiveSelectedDayKey);
    if (!view) return;
    dispatch(setDisplayScheduleState(view));
  }, [
    scheduleContentKey,
    currentFilters,
    effectiveSelectedDayKey,
    scheduleConstraints,
    tournamentInfo?.id,
    dispatch,
    schedule,
  ]);

  const handleFocusConflictOnGrid = useCallback((conflict: ScheduleConflict) => {
    const focusId = conflict.matchIds[1] ?? conflict.matchIds[0];
    setFocusedConflictMatchId(focusId);
    setFocusedConflictPeerIds([...conflict.matchIds]);
    setConflictFocusPulseGeneration((g) => g + 1);
  }, []);

  const handleNavigateConflictOnGrid = useCallback(
    (conflict: ScheduleConflict) => {
      const focusMatchId = conflict.matchIds[1] ?? conflict.matchIds[0];
      const dayKey = conflict.dayKey;
      const needsDaySwitch = selectedDayKey !== dayKey;

      handleFocusConflictOnGrid(conflict);

      if (needsDaySwitch) {
        suppressFocusClearOnDayChangeRef.current = true;
        setSelectedDayKey(dayKey);
      }

      scrollToScheduleMatchWithRetry(focusMatchId, {
        onComplete: (found) => {
          if (!found) {
            Notification(
              TypeError.info,
              t(prefix + 'scheduleConflictMatchNotOnGrid'),
              'bottom-right'
            );
          }
        },
      });
    },
    [handleFocusConflictOnGrid, prefix, selectedDayKey, t]
  );

  const handleViewConflictMatch = useCallback(
    (matchId: number) => {
      const match = flattenScheduledMatches(schedule).find((m) => m.id === matchId);
      if (!match) return;
      dispatch(
        openModal({
          currentModal: ModalAction.EDIT_MATCH_SCHEDULE,
          data: { match },
          modalOpen: true,
        })
      );
    },
    [dispatch, schedule]
  );

  useEffect(() => {
    if (suppressFocusClearOnDayChangeRef.current) {
      suppressFocusClearOnDayChangeRef.current = false;
      return;
    }
    setFocusedConflictMatchId(null);
    setFocusedConflictPeerIds([]);
  }, [selectedDayKey]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && focusedConflictMatchId != null) {
        setFocusedConflictMatchId(null);
        setFocusedConflictPeerIds([]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusedConflictMatchId]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      dismissScheduleToolbarFilterOnEscape(e);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const deselectItemOnClickOutsideScheduleItems = () => {
    if (selectedItem) {
      dispatch(setSelectedItem(null));
    }
    if (focusedConflictMatchId != null) {
      setFocusedConflictMatchId(null);
      setFocusedConflictPeerIds([]);
    }
  };

  const isMobile = useScheduleBreakpointMobile();

  const [zoomFloorsByDay, setZoomFloorsByDay] = useState<Record<number, ScheduleZoomFloors>>({});
  const scheduleColumnWidthRef = useRef(scheduleColumnWidth);
  scheduleColumnWidthRef.current = scheduleColumnWidth;
  const lastGridPointerRef = useRef<{ clientX: number; clientY: number } | null>(null);
  const clearPlacementRef = useRef<(() => void) | null>(null);

  const scheduleZoomFloors = useMemo(() => {
    const floors = Object.values(zoomFloorsByDay);
    if (floors.length === 0) return DEFAULT_SCHEDULE_ZOOM_FLOORS;
    return floors.reduce(mergeScheduleZoomFloors, DEFAULT_SCHEDULE_ZOOM_FLOORS);
  }, [zoomFloorsByDay]);

  const handleDayZoomFloorsChange = useCallback((dayKey: number, floors: ScheduleZoomFloors) => {
    setZoomFloorsByDay((prev) => {
      const current = prev[dayKey];
      if (current && areScheduleZoomFloorsEqual(current, floors)) return prev;
      return { ...prev, [dayKey]: floors };
    });
  }, []);

  useEffect(() => {
    const visibleDayKeys = new Set(
      displayViewStructureKey
        ? displayViewStructureKey.split('|').map((part) => Number(part.split(':')[0]))
        : []
    );
    setZoomFloorsByDay((prev) => {
      const nextEntries = Object.entries(prev).filter(([dayKey]) =>
        visibleDayKeys.has(Number(dayKey))
      );
      if (nextEntries.length === Object.keys(prev).length) return prev;
      return Object.fromEntries(nextEntries);
    });
  }, [displayViewStructureKey]);

  const applyScheduleScale = useCallback(
    (value: number) => {
      dispatch(
        setScheduleItemScaleMultiplier(
          clampScheduleScaleWithFloors(value, scheduleZoomFloors, { bumpToViewportFill: true })
        )
      );
    },
    [dispatch, scheduleZoomFloors]
  );

  const applyScheduleColumnWidth = useCallback(
    (value: number) => {
      dispatch(
        setScheduleColumnWidth(
          clampScheduleColumnWidthWithFloors(value, scheduleZoomFloors, {
            bumpToViewportFill: true,
          })
        )
      );
    },
    [dispatch, scheduleZoomFloors]
  );

  useEffect(() => {
    if (!displayViewStructureKey || !scheduleConstraints) return;
    const nextScale = clampScheduleScaleWithFloors(
      scheduleItemScaleMultiplier,
      scheduleZoomFloors,
      { bumpToViewportFill: true }
    );
    if (nextScale !== scheduleItemScaleMultiplier) {
      dispatch(setScheduleItemScaleMultiplier(nextScale));
    }
  }, [
    displayViewStructureKey,
    scheduleConstraints,
    scheduleZoomFloors,
    scheduleItemScaleMultiplier,
    dispatch,
  ]);

  useEffect(() => {
    if (!displayViewStructureKey || !scheduleConstraints) return;
    const currentWidth = scheduleColumnWidthRef.current;
    const nextWidth = clampScheduleColumnWidthWithFloors(currentWidth, scheduleZoomFloors, {
      bumpToViewportFill: true,
    });
    if (nextWidth !== currentWidth) {
      dispatch(setScheduleColumnWidth(nextWidth));
    }
  }, [displayViewStructureKey, scheduleConstraints, scheduleZoomFloors, dispatch]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) {
        if (isScheduleHistoryKeyTarget(e.target)) return;
      } else {
        const key = e.key.toLowerCase();
        if (key !== 'a' && isScheduleHistoryKeyTarget(e.target)) return;
      }

      if (e.key === 'Escape' && selectedMatchIds.length > 0) {
        dispatch(clearScheduleSelection());
        return;
      }

      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        selectedMatchIds.length >= 1 &&
        bulkSelectionHasScheduled &&
        !scheduleMutationBlocked
      ) {
        e.preventDefault();
        void dispatch(bulkRemoveMatchesFromScheduleAction(bulkSelectionScheduledIds, t));
        return;
      }

      if (!(e.ctrlKey || e.metaKey)) return;
      const key = e.key.toLowerCase();
      if (key === 'a') {
        if (dayBulkSelectableMatchIds.length === 0) return;
        if (isScheduleToolbarFilterTarget(e.target)) {
          blurScheduleToolbarFilters();
        }
        e.preventDefault();
        dispatch(setScheduleSelection(dayBulkSelectableMatchIds));
        return;
      }
      if (key === 'z') {
        if (scheduleMutationBlocked) return;
        if (e.shiftKey) {
          if (!canRedoSchedule) return;
          e.preventDefault();
          dispatch(redoScheduleChange());
        } else {
          if (!canUndoSchedule) return;
          e.preventDefault();
          dispatch(undoScheduleChange());
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    dispatch,
    canUndoSchedule,
    canRedoSchedule,
    scheduleMutationBlocked,
    selectedMatchIds,
    bulkSelectionHasScheduled,
    bulkSelectionScheduledIds,
    dayBulkSelectableMatchIds,
    t,
  ]);

  const handleSchedulePageClick = (e: React.MouseEvent) => {
    deselectItemOnClickOutsideScheduleItems();
    const target = e.target as HTMLElement;
    if (target.closest('[data-schedule-place-anchor]')) {
      return;
    }
    clearPlacementRef.current?.();
  };

  if (isCreatingSchedule) {
    return <CustomSpinner key="loading-spinner" />;
  }

  const formatEta = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  if (isGeneratingAsync) {
    return (
      <div
        className="p-5 text-center d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: '50vh' }}
      >
        <CustomSpinner key="loading-spinner" />
        <h3 className="mt-4">
          {t(prefix + 'scheduleGeneratingTitle', 'Generating your schedule...')}
        </h3>
        <div className="mt-3 p-3 bg-light rounded border shadow-sm" style={{ minWidth: '300px' }}>
          <h5 className="mb-2 text-primary">
            {jobQueuePosition === 0 ? 'Processing Now' : `Position in Queue: ${jobQueuePosition}`}
          </h5>
          <p className="mb-0 text-muted fw-bold">Estimated Time: ~{formatEta(jobEtaSeconds)}</p>
        </div>
        <p className="text-muted" style={{ maxWidth: '500px' }}>
          {t(
            prefix + 'scheduleGeneratingDesc',
            'Calculating schedule. The schedule will load when ready.'
          )}
        </p>

        <div className="mt-4">
          <CustomButton
            btnText={t(prefix + 'cancelScheduleGeneration', 'Cancel Generation')}
            variant="outline-danger"
            cb={() => dispatch(cancelScheduleAction(t))}
          />
        </div>
      </div>
    );
  }

  const showScheduleStatusBanner =
    scheduleMutationBlocked || (isFreeEditMode && !hasUnpublishedChanges);

  return (
    <div
      onClick={handleSchedulePageClick}
      key="schedule-layout"
      className={`schedule-page w-100 ${
        scheduleEditorFullscreen ? 'schedule-page--editor-fullscreen' : 'pb-5'
      } ${showScheduleStatusBanner ? 'schedule-page--read-only-banner' : ''}`}
    >
      {displayView && scheduleConstraints && (
        <>
          <ScheduleToolbar
            selectedDayKey={effectiveSelectedDayKey}
            onSelectedDayKeyChange={setSelectedDayKey}
            scheduleZoomFloors={scheduleZoomFloors}
            applyScheduleScale={applyScheduleScale}
            applyScheduleColumnWidth={applyScheduleColumnWidth}
            showConflictsToggleVisible={showConflictsToggleVisible}
            showScheduleConflicts={showScheduleConflicts}
            onShowScheduleConflictsChange={(value) => {
              setShowScheduleConflicts(value);
              if (value) setLeftRailTab('conflicts');
            }}
            blockInvalidPlacement={blockInvalidPlacement}
            onBlockInvalidPlacementChange={setBlockInvalidPlacement}
            scheduleEditingBlocked={scheduleMutationBlocked}
          />

          {scheduleMutationBlocked && (
            <div
              className="schedule-read-only-banner alert alert-info d-flex flex-wrap align-items-center justify-content-between gap-2 mb-0 mx-0 rounded-0 border-0 border-bottom"
              role="status"
            >
              <div>
                <div className="fw-semibold">{t(prefix + 'scheduleReadOnlyBannerTitle')}</div>
                <div className="small">{t(prefix + 'scheduleReadOnlyBannerBody')}</div>
              </div>
              <ScheduleToolbarButton
                variant="primary"
                shrink
                onClick={() => dispatch(setFreeEditMode(true))}
              >
                {t(prefix + 'scheduleEnableEditing')}
              </ScheduleToolbarButton>
            </div>
          )}

          {isFreeEditMode && !hasUnpublishedChanges && (
            <div
              className="schedule-read-only-banner alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-2 mb-0 mx-0 rounded-0 border-0 border-bottom"
              role="status"
            >
              <div className="small">{t(prefix + 'scheduleReadOnlyEditingActive')}</div>
              <ScheduleToolbarButton shrink onClick={() => dispatch(setFreeEditMode(false))}>
                {t(prefix + 'scheduleStopEditing')}
              </ScheduleToolbarButton>
            </div>
          )}

          {hasScheduleTimezoneStale && !timezoneStaleBannerDismissed && (
            <div
              className="schedule-read-only-banner alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-2 mb-0 mx-0 rounded-0 border-0 border-bottom"
              role="status"
            >
              <div>
                <div className="fw-semibold">{t(prefix + 'scheduleTimezoneStaleBannerTitle')}</div>
                <div className="small">{t(prefix + 'scheduleTimezoneStaleBannerBody')}</div>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <ScheduleToolbarButton
                  shrink
                  disabled={isSyncingScheduleTimezone || !communityId}
                  onClick={async () => {
                    if (!communityId) return;
                    setIsSyncingScheduleTimezone(true);
                    try {
                      const ok = await dispatch(
                        remediateScheduleTimezoneAction(t, parseInt(communityId, 10))
                      );
                      if (ok) {
                        setTimezoneStaleBannerDismissed(true);
                      }
                    } finally {
                      setIsSyncingScheduleTimezone(false);
                    }
                  }}
                >
                  {t(prefix + 'scheduleTimezoneStaleBannerAction')}
                </ScheduleToolbarButton>
                <ScheduleToolbarButton shrink onClick={() => setTimezoneStaleBannerDismissed(true)}>
                  {t(prefix + 'scheduleTimezoneStaleBannerDismiss')}
                </ScheduleToolbarButton>
              </div>
            </div>
          )}

          {hasOutOfWindowMatches && !outOfWindowBannerDismissed && (
            <div
              className="schedule-read-only-banner alert alert-warning d-flex flex-wrap align-items-center justify-content-between gap-2 mb-0 mx-0 rounded-0 border-0 border-bottom"
              role="status"
            >
              <div>
                <div className="fw-semibold">{t(prefix + 'scheduleOutOfWindowBannerTitle')}</div>
                <div className="small">{t(prefix + 'scheduleOutOfWindowBannerBody')}</div>
              </div>
              <ScheduleToolbarButton shrink onClick={() => setOutOfWindowBannerDismissed(true)}>
                {t(prefix + 'scheduleOutOfWindowBannerDismiss')}
              </ScheduleToolbarButton>
            </div>
          )}

          <div className="schedule-page__body">
            <ScheduleZoomRoot
              scheduleZoomFloors={scheduleZoomFloors}
              selectedDayKey={effectiveSelectedDayKey}
              onDayZoomFloorsChange={handleDayZoomFloorsChange}
              isMobile={isMobile}
              lastGridPointerRef={lastGridPointerRef}
              clearPlacementRef={clearPlacementRef}
              showScheduleConflicts={showScheduleConflicts}
              conflictMatchIdList={scheduleConflictMatchIdList}
              blockInvalidPlacement={blockInvalidPlacement}
              leftRailTab={leftRailTab}
              onLeftRailTabChange={setLeftRailTab}
              onSelectedDayKeyChange={setSelectedDayKey}
              focusedConflictMatchId={focusedConflictMatchId}
              focusedConflictPeerIds={focusedConflictPeerIds}
              conflictFocusPulseGeneration={conflictFocusPulseGeneration}
              onFocusConflictOnGrid={handleNavigateConflictOnGrid}
              onViewConflictMatch={handleViewConflictMatch}
            />
          </div>
        </>
      )}
    </div>
  );
};
